<template>
  <div>
    <transition name="modal">
      <rejection-modal
        v-if="showRejectModal"
        :loading="loadingReject"
        message="¿Estás seguro que deseas rechazar la solicitud? Por favor detalla cuál fue el problema."
        @close="showRejectModal = false"
        @confirm="reject($event)"
      />
    </transition>

    <transition name="modal">
      <confirm-with-notes-modal
        v-if="showNoChangeModal"
        :loading="loadingNoChange"
        message="Confirmar que la dieta actual sigue vigente y no se requiere ningún cambio. Se notificará al cliente y la solicitud se marcará como completada."
        notes-label="Comentarios para el cliente (opcional)"
        @close="showNoChangeModal = false"
        @confirm="confirmNoChange($event)"
      />
    </transition>

    <div v-if="!loading">
      <div v-if="quoteRequest" class="flex flex-col">
        <div class="card w-full">
          <div class="flex justify-between items-center">
            <p class="font-semibold">ID: #{{ quoteRequest.id }}</p>
          </div>

          <div class="div-line"></div>

          <p
            v-if="quoteRequest.status === 'pending'"
            class="p-1 text-xs bg-orange-500 w-fit rounded text-white"
          >
            Pendiente
          </p>

          <p
            v-else-if="quoteRequest.status === 'completed'"
            class="p-1 text-xs bg-success-500 w-fit rounded text-white"
          >
            Completada
          </p>

          <p
            v-else-if="quoteRequest.status === 'rejected'"
            class="p-1 text-xs bg-error-500 w-fit rounded text-white"
          >
            Rechazada
          </p>

          <p
            v-if="quoteRequest.source === 'reevaluation'"
            class="mt-2 p-1 text-xs bg-tertiary-500 w-fit rounded text-white"
          >
            Cotización por reevaluación
          </p>

          <div v-if="quoteRequest.status === 'rejected'" class="mt-2 text-sm">
            <p class="font-medium">Razón</p>
            <p>{{ quoteRequest.reject_reason }}</p>
          </div>

          <div
            v-if="quoteRequest.resolution === 'no_change'"
            class="mt-2 text-sm"
          >
            <p class="font-medium">Resolución</p>
            <p>
              Reevaluación atendida sin cambios — la dieta actual sigue vigente.
            </p>
            <p v-if="quoteRequest.resolution_notes" class="text-content-400">
              {{ quoteRequest.resolution_notes }}
            </p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Cliente</p>
            <router-link
              :to="{
                name: 'clientsDetail',
                params: { id: quoteRequest.client.id },
              }"
              class="underline system-button"
              >{{ quoteRequest.client.name }}
              {{ quoteRequest.client.last_name }}</router-link
            >
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Mascotas incluídas</p>
            <div class="flex flew-wrap">
              <div v-for="pet in quoteRequest.pets" :key="pet.id">
                <router-link
                  :to="{
                    name: 'petsDetail',
                    params: { id: pet.id },
                  }"
                  class="underline system-button mr-2"
                  >{{ pet.name }}</router-link
                >
              </div>
            </div>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Comentarios</p>
            <p>{{ quoteRequest.comments }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Fecha de creación</p>
            <p>{{ formatDatetime(quoteRequest.created_at) }}</p>
          </div>

          <div class="mt-2 text-sm">
            <p class="font-medium">Fecha de última actualización</p>
            <p>{{ formatDatetime(quoteRequest.updated_at) }}</p>
          </div>

          <router-link
            v-if="
              quoteRequest.status === 'pending' &&
              hasPermission('edit_quote_requests')
            "
            :to="{ name: 'quotesForm', params: { id: quoteRequest.id } }"
            class="secondary-button mt-4 mx-0"
          >
            Cotizar
          </router-link>

          <button
            v-if="
              quoteRequest.status === 'pending' &&
              quoteRequest.source === 'reevaluation' &&
              hasPermission('edit_quote_requests')
            "
            class="secondary-button mt-4 mx-0 sm:ml-2"
            @click="showNoChangeModal = true"
          >
            Confirmar sin cambios
          </button>

          <button
            v-if="
              quoteRequest.status === 'pending' &&
              hasPermission('edit_quote_requests')
            "
            class="mt-4 delete-button"
            @click="showRejectModal = true"
          >
            Rechazar
          </button>
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import RejectionModal from "@/components/ui/RejectionModal";
import ConfirmWithNotesModal from "@/components/ui/ConfirmWithNotesModal";
import deleteMixin from "@/mixins/deleteMixin";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Quote Request Detail",

  components: {
    RejectionModal,
    ConfirmWithNotesModal,
  },

  data() {
    return {
      showRejectModal: false,
      showNoChangeModal: false,
      quoteRequest: null,
      loading: false,
      loadingReject: false,
      loadingNoChange: false,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.$store.commit("setSelectedTab", "quoteRequests");

    this.loadQuoteRequest();
  },

  methods: {
    formatDatetime,

    integerToCurrency,

    async loadQuoteRequest() {
      this.loading = true;

      try {
        this.quoteRequest = await api.quoteRequests.getById(
          this.$route.params.id
        );
        this.$store.commit('setBreadcrumbLabel', {
          routeName: 'quoteRequestsDetail',
          label: 'Solicitud #' + this.quoteRequest.id,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "quoteRequests" });
      }

      this.loading = false;
    },

    async reject(reason) {
      this.loadingReject = true;

      try {
        const response = await api.quoteRequests.reject(this.quoteRequest.id, {
          reject_reason: reason,
        });
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$router.push({ name: 'quoteRequestsIndex' });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingReject = false;
      this.showRejectModal = false;
    },

    async confirmNoChange(notes) {
      this.loadingNoChange = true;

      try {
        const response = await api.quoteRequests.confirmNoChange(
          this.quoteRequest.id,
          { notes }
        );
        this.$store.dispatch("successSnack", response.message);
        this.showNoChangeModal = false;
        await this.loadQuoteRequest();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingNoChange = false;
    },
  },
};
</script>